import { useCallback, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { Company } from '@/types';
import { supabase } from '@/lib/supabase';
import { validatePasswordPolicy, friendlyPasswordError } from '@/lib/passwordPolicy';
import { api } from '@/services/api';
import { showAlertModal } from '@shared/components/common/AlertModal';

interface DashboardSecurityParams {
  company: Company;
}

export interface UseCompanyDashboardSecurityState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  isUpdatingPassword: boolean;
  setOldPassword: Dispatch<SetStateAction<string>>;
  setNewPassword: Dispatch<SetStateAction<string>>;
  setConfirmPassword: Dispatch<SetStateAction<string>>;
  handlePasswordUpdate: (event: FormEvent) => Promise<void>;
}

export const useCompanyDashboardSecurity = ({
  company
}: DashboardSecurityParams): UseCompanyDashboardSecurityState => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const handlePasswordUpdate = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!oldPassword || !newPassword || !confirmPassword) {
        setPasswordError('Please complete all password fields.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('Passwords do not match.');
        return;
      }
      const policyError = validatePasswordPolicy(newPassword);
      if (policyError) {
        setPasswordError(policyError);
        return;
      }

      setIsUpdatingPassword(true);
      setPasswordError('');

      try {
        const email = company.loggedInMember?.email || company.email;
        const { error: reauthError } = await supabase.auth.signInWithPassword({
          email,
          password: oldPassword
        });
        if (reauthError) {
          setPasswordError('Current password is incorrect.');
          return;
        }

        const memberId = company.loggedInMember?.id || 'primary';
        await api.updateCompanyPassword(company.id, memberId, newPassword);
        showAlertModal('Password updated successfully.');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (e: unknown) {
        setPasswordError(friendlyPasswordError(e));
      } finally {
        setIsUpdatingPassword(false);
      }
    },
    [
      company.loggedInMember?.email,
      company.loggedInMember?.id,
      company.id,
      company.email,
      confirmPassword,
      newPassword,
      oldPassword
    ]
  );

  return {
    oldPassword,
    newPassword,
    confirmPassword,
    passwordError,
    isUpdatingPassword,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    handlePasswordUpdate
  };
};
